import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiCheck, FiStar, FiTrendingUp, FiShield, FiZap, FiUsers, FiAward, FiCalendar, FiMail } = FiIcons;

const Home = () => {
  const services = [
    {
      title: "Siti Web Professionali",
      description: "Siti veloci, responsive e ottimizzati SEO che convertono visitatori in clienti",
      icon: FiZap,
      link: "/servizi/realizzazione-siti-web-napoli",
      features: ["Design responsive", "Ottimizzazione SEO", "Velocità elevata"]
    },
    {
      title: "E-commerce",
      description: "Shop online completi con pagamenti sicuri e gestione ordini automatizzata",
      icon: FiTrendingUp,
      link: "/servizi/realizzazione-ecommerce-napoli",
      features: ["Pagamenti sicuri", "Gestione magazzino", "Analytics vendite"]
    },
    {
      title: "Web Marketing",
      description: "Campagne Google Ads e Facebook per aumentare vendite e lead qualificati",
      icon: FiUsers,
      link: "/servizi/web-marketing-napoli",
      features: ["Google Ads", "Facebook Ads", "Lead generation"]
    },
    {
      title: "Consulenza SEO",
      description: "Posizionamento organico su Google per aumentare la visibilità online",
      icon: FiAward,
      link: "/servizi/consulenza-seo-napoli",
      features: ["Audit SEO", "Ottimizzazione contenuti", "Link building"]
    }
  ];

  const processSteps = [
    { step: "01", title: "Analisi", description: "Studiamo il tuo business e i competitor per definire la strategia migliore" },
    { step: "02", title: "Progettazione UX/UI", description: "Creiamo wireframe e design ottimizzati per la conversione" },
    { step: "03", title: "Sviluppo & SEO", description: "Sviluppiamo il sito con le migliori tecnologie e ottimizziamo per i motori di ricerca" },
    { step: "04", title: "Lancio & Crescita", description: "Lanciamo il progetto e monitoriamo le performance per miglioramenti continui" }
  ];

  const stats = [
    { number: "120+", label: "Progetti completati" },
    { number: "5 anni", label: "Esperienza media" }
  ];

  return (
    <>
      <Helmet>
        <title>Realizzazione Siti Web Napoli – Siti, E-commerce, SEO e Marketing</title>
        <meta name="description" content="Web Agency Napoli: Siti Web, E-Commerce, Ottimizzazione SEO, Gestione Social, Web Marketing. Preventivo gratuito in 24h. Aiutiamo PMI e professionisti a crescere online." />
        <meta name="keywords" content="realizzazione siti web napoli, web agency napoli, siti internet napoli, e-commerce napoli, seo napoli, marketing digitale napoli" />
        <link rel="canonical" href="https://realizzazionesitiwebnapoli.com/" />
        {/* Open Graph */}
        <meta property="og:title" content="Realizzazione Siti Web Napoli – Siti, E-commerce, SEO e Marketing" />
        <meta property="og:description" content="Web Agency Napoli: Siti Web, E-Commerce, Ottimizzazione SEO, Gestione Social, Web Marketing. Preventivo gratuito in 24h." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://realizzazionesitiwebnapoli.com/" />
        <meta property="og:image" content="https://realizzazionesitiwebnapoli.com/images/og-home.jpg" />
        <meta property="og:locale" content="it_IT" />
        <meta property="og:site_name" content="Realizzazione Siti Web Napoli" />
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://realizzazionesitiwebnapoli.com/#organization",
                "name": "Realizzazione Siti Web Napoli",
                "url": "https://realizzazionesitiwebnapoli.com/",
                "logo": { "@type": "ImageObject", "url": "https://realizzazionesitiwebnapoli.com/images/logo.png" }
              },
              {
                "@type": "WebSite",
                "@id": "https://realizzazionesitiwebnapoli.com/#website",
                "url": "https://realizzazionesitiwebnapoli.com/",
                "name": "Realizzazione Siti Web Napoli",
                "description": "Web Agency Napoli: Siti Web, E-Commerce, Ottimizzazione SEO, Gestione Social, Web Marketing."
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen bg-napoli-gradient text-white overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1920&auto=format&fit=crop')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-black opacity-40" />
        <div className="relative container mx-auto px-4 py-12 text-center z-10">
          <div className="max-w-4xl mx-auto">
            <motion.h1 
              style={{ color: 'rgb(255, 255, 255)', paddingTop: '20px', paddingBottom: '20px' }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl lg:text-6xl font-poppins font-bold mb-6 text-white drop-shadow-lg mt-12 md:mt-0"
            >
              Siti web ed e-commerce che portano clienti veri a <span className="text-napoli-accent drop-shadow-md">Napoli</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl lg:text-2xl mb-8 text-white leading-relaxed drop-shadow-md"
            >
              Aiutiamo PMI, professionisti e startup di Napoli a crescere online con siti web veloci, e-commerce che convertono e strategie di marketing digitale su misura.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
            >
              <Link to="/contatti" className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg text-lg">
                Preventivo gratuito in 24h <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2 inline" />
              </Link>
              <a href="https://tidycal.com/sitiwebstudio/consulenza-siti-web-napoli" target="_blank" rel="noopener noreferrer" className="bg-napoli-azzurro hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg text-lg">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 inline" /> Prenota una consulenza
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sezione Servizi */}
      <section className="py-16 bg-napoli-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-napoli-blu mb-4">Servizi per far crescere il tuo business online</h2>
            <p className="text-xl text-napoli-blu max-w-3xl mx-auto font-semibold">Soluzioni complete per PMI, professionisti e startup di Napoli</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-napoli hover:shadow-napoli-lg transition-all duration-300 group hover:-translate-y-2"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-napoli-azzurro to-napoli-accent rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={service.icon} className="w-6 h-6 text-napoli-blu" />
                </div>
                <h3 className="text-xl font-poppins font-semibold text-napoli-blu mb-3">{service.title}</h3>
                <p className="text-napoli-blu mb-4 font-semibold">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-napoli-blu font-semibold">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-napoli-accent mr-2" /> {feature}
                    </li>
                  ))}
                </ul>
                <Link to={service.link} className="inline-flex items-center text-napoli-azzurro hover:text-napoli-blu font-bold transition-colors">
                  Scopri di più <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;